/**
 * Zod schemas + DTO types for the section-catalog API surface — Block 2
 * «Трекер объектов», Этап 2 (каталог §5.2). A SectionCatalogItem is a node in
 * the fixed, supervisor-editable «справочник разделов проектирования»: a
 * two-level tree where `parentId = null` is a GROUP («ЭОМ») and `parentId` set
 * is a SUBSECTION. Project sections are later PICKED from this catalog (the
 * picker feeds `Section.catalogItemId` + a name snapshot — KD-5).
 *
 * Contract split (mirrors `section.ts` / `expenses.ts`):
 *   - READ  (`GET /section-catalog`) — the TREE. Open to any authenticated
 *     actor (the picker needs РО/ГИП/ADMIN, the editor needs the supervisor).
 *   - WRITE (`POST`/`PATCH`/`DELETE`) — SUPERVISOR-only (enforced by the guard).
 *
 * PERMANENT RULE [R2]: `parentId` appears ONLY in the CREATE DTO. It is absent
 * from the update DTO (immutable — no re-parent), which makes depth-3
 * STRUCTURALLY impossible: a subsection can never acquire a child. The service
 * additionally rejects a create whose `parentId` points at a subsection (422).
 *
 * Name-collision (FE↔BE contract) [R2:da#2]: on a duplicate ACTIVE name the BE
 * returns a FIELD-LEVEL 422 with code `CATALOG_NAME_TAKEN` — NOT a 409. The FE
 * branches on the CODE, not the HTTP status: a 409 is the optimistic-lock
 * «Запись изменилась» path, and mislabelling a name clash as a version conflict
 * would confuse the editor. The 422 body carries `details.fieldErrors.name` so
 * the shared `applyApiFieldErrors` helper renders it inline on the «name» field.
 */
import { z } from 'zod';

// ─── Shared primitives ────────────────────────────────────────────────────

const UuidSchema = z.string().uuid();

/** ISO-8601 datetime (createdAt / updatedAt / archivedAt). */
const IsoDateTimeSchema = z.string().datetime({ offset: true });

/**
 * Catalog item name — 1..300 chars after trim. The 300 cap (vs the 200 used
 * for `Section` / `ProjectExpense` labels) mirrors the DB CHECK
 * `char_length(name) BETWEEN 1 AND 300` in migration 038: the longest §5.2
 * subsection is 192 chars, so a blind `1..200` clone would reject the seed.
 */
const CatalogNameSchema = z
  .string()
  .trim()
  .min(1, { message: 'Укажите название' })
  .max(300, { message: 'Название не должно превышать 300 символов' });

/** Sort order — non-negative int, capped well below Int4 to avoid abuse. */
const SortOrderSchema = z.number().int().min(0).max(100_000);

// ─── Error codes (FE↔BE contract) ──────────────────────────────────────────

/**
 * Name-collision error code (422, field-level). Emitted by the service on a
 * duplicate ACTIVE group name, or a duplicate ACTIVE child name under the same
 * parent. The FE branches on this code (NOT the HTTP status) to render an inline
 * field error rather than the 409 version-conflict toast. `[R2:da#2]`
 */
export const CATALOG_NAME_TAKEN = 'CATALOG_NAME_TAKEN' as const;

// ─── Write requests ───────────────────────────────────────────────────────

/**
 * `POST /section-catalog` — create a GROUP (`parentId` null/absent) or a
 * SUBSECTION (`parentId` = an existing group id). `parentId` is CREATE-ONLY
 * (permanent rule — never in the update DTO). The service validates that a
 * supplied `parentId` references an ACTIVE GROUP (depth ≤2); a `parentId`
 * pointing at a subsection is rejected 422 (depth-3 forbidden).
 */
export const CreateCatalogItemRequestSchema = z
  .object({
    name: CatalogNameSchema,
    parentId: UuidSchema.nullable().optional(),
    sortOrder: SortOrderSchema.optional(),
  })
  .strict();
export type CreateCatalogItemRequest = z.infer<typeof CreateCatalogItemRequestSchema>;

/**
 * `PATCH /section-catalog/:id` — rename / reorder. `version` REQUIRED
 * (optimistic-lock → 409 on mismatch). `parentId` is DELIBERATELY ABSENT
 * (immutable — see the module header): re-parenting is impossible, so depth-3 is
 * structurally impossible. At least one mutable field beyond `version` is
 * required so a no-op PATCH is rejected.
 */
export const UpdateCatalogItemRequestSchema = z
  .object({
    version: z.number().int().nonnegative(),
    name: CatalogNameSchema.optional(),
    sortOrder: SortOrderSchema.optional(),
  })
  .strict()
  .refine((dto) => dto.name !== undefined || dto.sortOrder !== undefined, {
    message: 'Нет изменений для сохранения',
  });
export type UpdateCatalogItemRequest = z.infer<typeof UpdateCatalogItemRequestSchema>;

// ─── Response shapes ──────────────────────────────────────────────────────

/**
 * A single catalog node. `children` is present (and populated) for a GROUP in
 * the tree response; it is omitted for a subsection and on single-node mutation
 * responses. The catalog is reference-data — there is NO `createdById` (the
 * acting supervisor is recorded in the global AuditLog).
 */
export interface CatalogItemResponse {
  id: string;
  parentId: string | null;
  name: string;
  sortOrder: number;
  version: number;
  archivedAt: string | null;
  createdAt: string;
  updatedAt: string;
  /**
   * Невидимая метка служебной строки справочника — Ф5 · В9-d, решение **Р-93**
   * (ответы 1 и 5). `null` у всего, что завёл человек, — это ЗАКОННОЕ
   * состояние, а не пропуск разметки.
   *
   * ⚠️ ПРИЗНАК ЖИВЁТ В ДОГОВОРЕ, А НЕ ВЫВОДИТСЯ ЭКРАНОМ ИЗ ИМЕНИ, И ЭТО
   * ОПЛАЧЕНО. Цена ответа 1, названная владельцу ДО ответа и им принятая,
   * звучала «признака „служебная группа" в справочнике НЕТ, его надо завести
   * и научить экран фильтровать». Отбор по имени (`name === 'Внутренние
   * работы'`) признака не требует вовсе — то есть выбранный вариант был бы
   * куплен не тем, за что заплачено, и разъехался бы с базой при первом же
   * переименовании группы.
   *
   * ⚠️ В ЗАПИСЬ НЕ ПРИНИМАЕТСЯ НИ ОДНИМ DTO — `CreateCatalogItemRequest` и
   * `UpdateCatalogItemRequest` объявлены `.strict()` и поля не несут. Метку
   * ставит только миграция `061` и загрузчик; иначе супервайзер мог бы снять
   * её с несносимого вида (или подделать на своём) и отменить ответ 2 Р-93
   * одним PATCH-ом.
   *
   * ⚠️ «Невидимая» — про ЭКРАНЫ, а не про JSON. Ни справочник супервайзера,
   * ни пикер разделов, ни бот метку не показывают (пара 5 приёмки Р-93);
   * договор её несёт затем, чтобы экран мог по ней ОТФИЛЬТРОВАТЬ.
   */
  systemKey: string | null;
  /** Populated for a GROUP in the tree response; omitted otherwise. */
  children?: CatalogItemResponse[];
}

/**
 * Recursive schema for a catalog node. `z.lazy` + the explicit `z.ZodType`
 * annotation is the canonical zod pattern for a self-referential shape. Used
 * for type inference and FE-side response parsing; the BE does not re-validate
 * its own responses.
 */
export const CatalogItemResponseSchema: z.ZodType<CatalogItemResponse> = z.lazy(() =>
  z.object({
    id: UuidSchema,
    parentId: UuidSchema.nullable(),
    name: z.string(),
    sortOrder: z.number().int(),
    version: z.number().int().nonnegative(),
    archivedAt: IsoDateTimeSchema.nullable(),
    createdAt: IsoDateTimeSchema,
    updatedAt: IsoDateTimeSchema,
    systemKey: z.string().nullable(),
    children: z.array(CatalogItemResponseSchema).optional(),
  }),
);

/**
 * `GET /section-catalog` response — the catalog TREE. `data` is the ordered list
 * of GROUPS, each carrying its ordered `children` (active by default; archived
 * included only when the editor requests `?includeArchived=true`).
 */
export const CatalogTreeResponseSchema = z.object({
  data: z.array(CatalogItemResponseSchema),
});
export type CatalogTreeResponse = z.infer<typeof CatalogTreeResponseSchema>;
